'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

import { usePageLayout } from '@libs/common-next';
import type { RelatedProductItem } from '@libs/common-types';

import { fetchRecommendedProducts, normalizeRecommendedProducts } from '../api';
import {
    getRecommendedProductsCacheKey,
    readCachedRecommendedProducts,
    writeCachedRecommendedProducts,
} from '../lib/recommendedProductsCache';

const DEFAULT_STORE_ID = '0';
const DEFAULT_CUSTOMER_ID = '0';

export type UseRecommendedProductsOptions = {
    /** SKUs to hide from results (e.g. items already in cart). Applied after fetch/cache. */
    excludeProductIds?: string[];
    /**
     * When true (default), freeze the first non-empty productIds snapshot (PDP YMAL).
     * When false, refetch whenever the productIds set changes (cart).
     */
    freezeInitial?: boolean;
    /**
     * When true (default), read/write sessionStorage (PDP).
     * Cart sets false so add/remove/load always hit the recommendations API instead of
     * reusing a PDP-warmed `recommended_products:{sku}:0` cache entry.
     */
    useSessionCache?: boolean;
};

const toSortedUniqueIds = (productIds?: string[]): string[] => {
    if (!productIds?.length) return [];
    return [...new Set(productIds.map(id => id.trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
};

/** Fetches recommendation products for one or more anchor SKUs. Domain-neutral — used by PDP YMAL and Cart FBT wrappers. */
export const useRecommendedProducts = (
    isEnabled: boolean,
    productIds?: string[],
    options: UseRecommendedProductsOptions = {}
): RelatedProductItem[] => {
    const { excludeProductIds, freezeInitial = true, useSessionCache = true } = options;
    const { user } = usePageLayout();

    const frozenIdsRef = useRef<string[] | null>(null);
    const requestedKeyRef = useRef<string | null>(null);

    const incomingKey = toSortedUniqueIds(productIds).join(',');
    const incomingIds = useMemo(() => (incomingKey ? incomingKey.split(',') : []), [incomingKey]);

    if (freezeInitial && frozenIdsRef.current === null && incomingIds.length) {
        frozenIdsRef.current = incomingIds;
    }

    const activeIds = freezeInitial ? (frozenIdsRef.current ?? []) : incomingIds;
    const productIdsKey = activeIds.join(',');

    const [products, setProducts] = useState<RelatedProductItem[]>([]);
    const [resolvedProductIdsKey, setResolvedProductIdsKey] = useState<string | null>(null);

    useEffect(() => {
        let stale = false;

        if (!isEnabled) {
            setProducts([]);
            setResolvedProductIdsKey(null);
            requestedKeyRef.current = null;
            return;
        }

        if (!productIdsKey) {
            setProducts([]);
            setResolvedProductIdsKey(null);
            return;
        }

        const ids = productIdsKey.split(',');
        const customerId = String(user?.customerprofileid ?? DEFAULT_CUSTOMER_ID);
        const requestKey = `${productIdsKey}:${customerId}`;

        // Dedupe only while an in-flight attempt owns this key. Always clear on cleanup so
        // React Strict Mode (effect → cleanup → effect) can retry instead of returning empty.
        if (requestedKeyRef.current === requestKey) return;
        requestedKeyRef.current = requestKey;
        setProducts([]);
        setResolvedProductIdsKey(null);

        const cacheKey = getRecommendedProductsCacheKey(ids, customerId);

        if (useSessionCache) {
            const cached = readCachedRecommendedProducts(cacheKey);
            if (cached?.length) {
                setProducts(cached);
                setResolvedProductIdsKey(productIdsKey);
                return () => {
                    stale = true;
                    if (requestedKeyRef.current === requestKey) {
                        requestedKeyRef.current = null;
                    }
                };
            }
        }

        fetchRecommendedProducts({ productIds: ids, storeId: DEFAULT_STORE_ID, customerId })
            .then(hits => {
                if (stale) return;

                const normalized = normalizeRecommendedProducts(hits);

                if (useSessionCache && normalized.length) {
                    writeCachedRecommendedProducts(cacheKey, normalized);
                }
                setProducts(normalized);
                setResolvedProductIdsKey(productIdsKey);
            })
            .catch(() => {
                if (stale) return;

                requestedKeyRef.current = null;
                setProducts([]);
                setResolvedProductIdsKey(null);
            });

        return () => {
            stale = true;
            if (requestedKeyRef.current === requestKey) {
                requestedKeyRef.current = null;
            }
        };
    }, [isEnabled, user?.customerprofileid, productIdsKey, useSessionCache]);

    const productsForActiveIds = resolvedProductIdsKey === productIdsKey ? products : [];

    return useMemo(() => {
        if (!excludeProductIds?.length) return productsForActiveIds;

        const exclude = new Set(excludeProductIds);
        return productsForActiveIds.filter(product => !exclude.has(product.sku));
    }, [productsForActiveIds, excludeProductIds]);
};
